'use client';

import {
  useContext,
  type FC,
  type ReactNode,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { SectionsSwitchProps } from './SectionsSwitch.types';
import { QuestionnaireDocumentChecklistSection } from '../QuestionnaireDocumentChecklistSection';
import { ReviewSection } from '../ReviewSection';
import { CustomSection } from '../CustomSection';
import { QuestionnaireSection } from './_components/QuestionnaireSection';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { QuestionnaireLetterSection } from './_components/QuestionnaireLetterSection';
import { saveSection } from '@/_api/client-questionnaires';
import { UserContext } from '@/_contexts/UserProvider';
import { usePrevious } from '@/_hooks/usePrevious';
import { Section } from '@/_types/models/sections';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { LoadingSection } from './_components/LoadingSection';

export const SectionsSwitch: FC<SectionsSwitchProps> = ({
  isLoading,
  isReviewSection,
  mode,
  questionnaire,
  questions,
  section,
}) => {
  const { isDisabled, updateSection } = useContext(ClientQuestionnaireContext);
  const { token, isUserReadOnly, isCompanyReadOnly } = useContext(UserContext);
  const { onMarkSectionComplete } = useContext(UserPreviewContext);

  const _getIsCustomSection = useCallback(
    (s: Section | Partial<Section> | undefined) =>
      s?.section_key === 'custom' || s?.section_type === 0,
    [],
  );

  const isViewOnly: boolean = isDisabled || isUserReadOnly || isCompanyReadOnly;

  const previousSectionId: number | undefined = usePrevious(section?.id);
  const isPreviousSectionCustom: boolean = usePrevious(
    _getIsCustomSection(section),
  );

  const isCustomSection: boolean = useMemo(
    () => _getIsCustomSection(section),
    [_getIsCustomSection, section],
  );

  const sectionInstruction: string | undefined = section?.instruction;

  useEffect(() => {
    if (isPreviousSectionCustom) {
      const markCompleted = async () => {
        try {
          if (!previousSectionId) return;
          if (mode === 'admin-template') {
            return onMarkSectionComplete(previousSectionId);
          }

          updateSection(previousSectionId, { marked_completed: true });
          saveSection({
            companyId: questionnaire?.company_id!,
            id: previousSectionId!,
            markedCompleted: true,
            setId: questionnaire?.id!,
            token,
          });
        } catch (error) {
          console.error(error);
        }
      };

      markCompleted();
    }
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO @chris API should only depend either on `section_type` or `section_key`
  const getSectionComponent = (): ReactNode => {
    const isAgreementSignature: boolean =
      section?.section_key === 'agreement_signature' ||
      section?.section_type === 2;
    const isDocumentChecklist: boolean =
      section?.section_key === 'document_checklist' ||
      section?.section_type === 10;
    const isReviewAndSubmit: boolean =
      section?.section_key === 'review_and_submit' ||
      section?.section_type === 4;

    if (isLoading) {
      return <LoadingSection />;
    }

    if (isAgreementSignature) {
      return <QuestionnaireLetterSection section={section} mode={mode} />;
    }

    // TODO @chris [SOR-11811] separate PR
    if (isDocumentChecklist) {
      return (
        <QuestionnaireDocumentChecklistSection
          section={section}
          mode={mode}
          isReviewSection={isReviewSection}
          isLocked={isViewOnly}
          questionnaire={questionnaire}
        />
      );
    }

    if (isCustomSection) {
      return <CustomSection section={section} showInstruction={true} />;
    }

    if (isReviewAndSubmit) {
      return <ReviewSection mode={mode} instruction={sectionInstruction} />;
    }

    return (
      <QuestionnaireSection
        questions={questions}
        isDisabled={isViewOnly}
        isReviewSection={isReviewSection}
        mode={mode}
        instruction={sectionInstruction}
      />
    );
  };

  return getSectionComponent();
};
